import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';

import { extractUnitAndMetric, getClassNames } from '../../../../utils/util';
import {
  ACTIVE_LISTING,
  ARCHIVED_LISTING,
  COPY_LISTING,
  COPY_LISTING_DRAFT,
  DRAFT_LISTING,
  NOT_SURE,
  PENDING_LISTING,
} from '../../../../utils/constants';

import Dropdown from '../../../common/inputs/dropdown/Dropdown';
import TextInput from '../../../common/inputs/text-input/TextInput';
import DatePicker from '../../../common/inputs/date-picker/DatePicker';
import FilePicker from '../../../common/inputs/file-picker/FilerPicker';
import Button from '../../../common/button/Button';
import Image from '../../../common/image/Image';
import ImageCarousel from '../../../common/image-carousel/ImageCarousel';

import { ReactComponent as TrashIcon } from '../../../../resources/icons/trash.svg';

import './upload-product-listing.scss';

const UploadProductListing = ({
  listing,
  classifications,
  categories,
  types,
  metricUnits,
  retrieveCategories,
  retrieveTypes,
  retrieveMetricUnits,
  createProductListing,
  updateProductListing,
  copyListing,
  showErrorToast,
  currencyCode,
}) => {
  const [classification, setClassification] = useState(undefined);
  const [category, setCategory] = useState(undefined);
  const [type, setType] = useState(undefined);
  const [brandName, setBrandName] = useState('');
  const [unit, setUnit] = useState('');
  const [metric, setMetric] = useState(undefined);
  const [unitPrice, setUnitPrice] = useState('');
  const [totalQuantity, setTotalQuantity] = useState('');
  const [minimumQuantity, setMinimumQuantity] = useState('');
  const [offerAvailableFrom, setOfferAvailableFrom] = useState(undefined);
  const [offerExpiresOn, setOfferExpiresOn] = useState(undefined);
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('');
  const [images, setImages] = useState([]);
  const [additionalInformation, setAdditionalInformation] = useState('');
  const [errors, setErrors] = useState({});
  const [selectedImageIndex, setSelectedImageIndex] = useState(undefined);

  const fileRef = useRef(null);

  const navigate = useNavigate();

  useEffect(() => {
    if (!listing) return;

    const { classification } = listing;
    setClassification(classification);
    retrieveCategories(classification.externalReference);
  }, [listing, retrieveCategories]);

  useEffect(() => {
    if (!listing) return;

    const { category } = listing;
    setCategory(category);
    retrieveTypes(category.externalReference);
  }, [listing, retrieveTypes]);

  useEffect(() => {
    if (!listing) return;

    const { specification } = listing;
    setType(specification);
    retrieveMetricUnits(specification.externalReference);
  }, [listing, retrieveMetricUnits]);

  useEffect(() => {
    if (!listing) return;

    const [unit, unitMetric] = extractUnitAndMetric(listing.productUnit);
    setUnit(unit);
    setMetric({ label: unitMetric, value: unitMetric });

    const [totalQuantity] = extractUnitAndMetric(listing.totalAmount);
    setTotalQuantity(totalQuantity);

    const [minimumQuantity] = extractUnitAndMetric(listing.minimumOrderQuantity);
    setMinimumQuantity(minimumQuantity);

    setBrandName(listing.brandName ?? '');
    setUnitPrice(parseFloat(listing.pricePerProductUnit?.split(' ')[0] ?? '').toFixed(2));
    setOfferAvailableFrom(listing.offerAvailableFrom ? new Date(listing.offerAvailableFrom) : undefined);
    setOfferExpiresOn(listing.offerExpiresOn ? new Date(listing.offerExpiresOn) : undefined);
    setCity(listing.city ?? '');
    setCountry(listing.country ?? '');
    setImages(listing.linkedMediaExternalReferences ?? []);
    setAdditionalInformation(listing.additionalInformation ?? '');
  }, [listing]);

  const handleClassificationChange = (classification) => {
    setClassification(classification);
    retrieveCategories(classification.externalReference);

    if (classification.externalReference === NOT_SURE) {
      handleCategoryChange(classification);
      return;
    }

    setCategory(null);
    setType(null);
    setMetric(null);
  };

  const setImageErrorFalse = () => {
    setErrors((errors) => ({ ...errors, images: false }));
  };

  const handleCategoryChange = (category) => {
    setCategory(category);
    retrieveTypes(category.externalReference);

    if (category.externalReference === NOT_SURE) {
      handleTypeChange(category);
      return;
    }

    setType(null);
    setMetric(null);
  };

  const handleTypeChange = (type) => {
    setType(type);
    retrieveMetricUnits(type.externalReference);
    setMetric(null);
  };

  const handleFileChange = (file) => {
    setImages([...images, file]);
  };

  const handleDeleteImageClick = (event, index) => {
    event.stopPropagation();
    const newImages = [...images];
    newImages.splice(index, 1);
    setImages(newImages);
  };

  const validateFields = (status) => {
    const errors = {};

    if (!classification) errors.classification = true;
    if (!category) errors.category = true;
    if (!type) errors.type = true;
    if (!unit) errors.unit = true;
    if (!metric) errors.metric = true;
    if (!unitPrice) errors.unitPrice = true;
    if (!totalQuantity) errors.totalQuantity = true;
    if (!minimumQuantity) errors.minimumQuantity = true;
    if (!offerAvailableFrom) errors.offerAvailableFrom = true;
    if (!offerExpiresOn) errors.offerExpiresOn = true;
    if (!city) errors.city = true;
    if (!country) errors.country = true;
    if (status !== DRAFT_LISTING) {
      if (!images || images.length < 1) {
        if (Object.keys(errors).length === 0) {
          showErrorToast('Please upload an image or save as draft');
        }
        errors.images = true;
      }
    }

    setErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveClick = (status) => {
    const valid = validateFields(status);
    if (!valid) return;

    let productSpecificationExternalReference = null;

    if (type.externalReference !== NOT_SURE) {
      productSpecificationExternalReference = type.externalReference;
    }

    const productListing = {
      singleUnitSize: unit,
      singleUnitMetric: metric?.value,
      singleUnitPrice: unitPrice,
      totalUnit: totalQuantity,
      totalUnitMetric: metric?.value,
      minimumOrderQuantity: minimumQuantity,
      minimumOrderQuantityMetric: metric?.value,
      photos: images,
      productClassificationName: classification.name,
      productCategoryName: category.name,
      productSpecificationName: type.name,
      productSpecificationExternalReference,
      additionalInformation,
      offerAvailableFrom,
      offerExpiresOn,
      brandName,
      country,
      status,
      city,
    };

    if (!listing) {
      createProductListing(productListing);
    } else if (listing && status === COPY_LISTING) {
      productListing.status = PENDING_LISTING;
      copyListing(productListing);
    } else if (listing && status === COPY_LISTING_DRAFT) {
      productListing.status = DRAFT_LISTING;
      copyListing(productListing);
    } else {
      updateProductListing(productListing);
    }
  };

  const renderImages = () => {
    return images.map((image, index) => {
      const key = typeof image === 'string' ? image : image.name;
      return (
        <div
          key={key}
          className="upload-product-listing__form__photo-container"
          onClick={() => setSelectedImageIndex(index)}
        >
          {typeof image === 'string' ? (
            <Image className="upload-product-listing__form__photo" externalReference={image} alt="" />
          ) : (
            <Image className="upload-product-listing__form__photo" file={image} alt="" />
          )}
          <TrashIcon onClick={(event) => handleDeleteImageClick(event, index)} data-testid="delete-image-button" />
        </div>
      );
    });
  };

  const renderPrimaryButton = () => {
    let text;
    let status;

    if (!listing) {
      text = 'Upload';
      status = PENDING_LISTING;
    } else if (listing && listing.status === DRAFT_LISTING) {
      text = 'Activate Listing';
      status = PENDING_LISTING;
    } else if (listing && listing.status === ARCHIVED_LISTING) {
      text = 'Copy and Renew Listing';
      status = COPY_LISTING;
    } else {
      text = 'Update';
    }

    return <Button onClick={() => handleSaveClick(status)}>{text}</Button>;
  };

  const renderSecondaryButton = () => {
    let text;
    let status;

    if (listing && listing.status === DRAFT_LISTING) {
      text = 'Update';
      status = DRAFT_LISTING;
    } else if (listing && listing.status === ACTIVE_LISTING) {
      text = 'Archive';
      status = ARCHIVED_LISTING;
    } else if (listing && listing.status === ARCHIVED_LISTING) {
      text = 'Copy as Draft';
      status = COPY_LISTING_DRAFT;
    } else {
      text = 'Save as Draft';
      status = DRAFT_LISTING;
    }

    return (
      <Button onClick={() => handleSaveClick(status)} secondary>
        {text}
      </Button>
    );
  };

  return (
    <>
      <section className="upload-product-listing" data-testid="upload-product-listing">
        <section className="upload-product-listing__card">
          <h1>Upload Product Listing</h1>
          <section className="upload-product-listing__form">
            <p>Product Details</p>

            <label htmlFor="product-classification">Product Classification</label>
            <Dropdown
              name="product-classification"
              value={classification}
              options={classifications}
              onChange={handleClassificationChange}
              getOptionLabel={(option) => option.name}
              getOptionValue={(option) => option.externalReference}
              error={errors.classification}
              noSort
            />

            <label htmlFor="product-category">Product Category</label>
            <Dropdown
              name="product-category"
              value={category}
              options={categories}
              onChange={handleCategoryChange}
              disabled={!classification}
              getOptionLabel={(option) => option.name}
              getOptionValue={(option) => option.externalReference}
              error={errors.category}
              noSort
            />

            <label htmlFor="product-type">Product Type</label>
            <Dropdown
              name="product-type"
              value={type}
              options={types}
              onChange={handleTypeChange}
              disabled={!category}
              getOptionLabel={(option) => option.name}
              getOptionValue={(option) => option.externalReference}
              error={errors.type}
              noSort
            />

            <label htmlFor="product-name">Product Brand Name</label>
            <TextInput
              name="product-name"
              placeholder="Leave blank if product is generic"
              value={brandName}
              onChange={({ target }) => setBrandName(target.value)}
            />

            <section className="upload-product-listing__form__grid">
              <section className="upload-product-listing__form__grid__item">
                <label htmlFor="product-unit">One Product Unit is (Unit Volume Metric)</label>
                <div>
                  <TextInput
                    name="product-unit"
                    value={unit}
                    onChange={({ target }) => setUnit(target.value)}
                    error={errors.unit}
                    numberOnly
                  />
                  <Dropdown
                    value={metric}
                    options={metricUnits}
                    onChange={(option) => setMetric(option)}
                    disabled={!type}
                    error={errors.metric}
                  />
                </div>
              </section>

              <section className="upload-product-listing__form__grid__item">
                <label htmlFor="product-price">Price per Product Unit is</label>
                <div>
                  <TextInput
                    name="product-price"
                    value={unitPrice}
                    onChange={({ target }) => setUnitPrice(target.value)}
                    error={errors.unitPrice}
                    numberOnly
                  />
                  <p>{currencyCode}.</p>
                </div>
                {currencyCode !== 'USD' && (
                  <div className="upload-product-listing__price-disclaimer">
                    <p className="upload-product-listing__price-disclaimer__text">*</p>
                    <p className="upload-product-listing__price-disclaimer__text">
                      Please note that while you have selected to view all prices in {currencyCode}, all prices are
                      recorded in USD as per the exchange rate at the time of uploading this product listing.
                    </p>
                  </div>
                )}
              </section>

              <section className="upload-product-listing__form__grid__item">
                <label htmlFor="product-quantity">Total Amount of product available is</label>
                <div>
                  <TextInput
                    name="product-quantity"
                    value={totalQuantity}
                    onChange={({ target }) => setTotalQuantity(target.value)}
                    error={errors.totalQuantity}
                    numberOnly
                  />
                  <Dropdown
                    value={metric}
                    options={metricUnits}
                    onChange={(option) => setMetric(option)}
                    disabled={!type}
                    error={errors.metric}
                  />
                </div>
              </section>

              <section className="upload-product-listing__form__grid__item">
                <label htmlFor="product-minimum-quantity">Minimum Order Quantity is</label>
                <div>
                  <TextInput
                    name="product-minimum-quantity"
                    value={minimumQuantity}
                    onChange={({ target }) => setMinimumQuantity(target.value)}
                    error={errors.minimumQuantity}
                    numberOnly
                  />
                  <Dropdown
                    value={metric}
                    options={metricUnits}
                    onChange={(option) => setMetric(option)}
                    disabled={!type}
                    error={errors.metric}
                  />
                </div>
              </section>

              <section className="upload-product-listing__form__grid__item">
                <label htmlFor="product-offer-start-date">Offer available from</label>
                <DatePicker
                  name="product-offer-start-date"
                  value={offerAvailableFrom}
                  onChange={(date) => setOfferAvailableFrom(date)}
                  error={errors.offerAvailableFrom}
                  maxDate={offerExpiresOn}
                />
              </section>

              <section className="upload-product-listing__form__grid__item">
                <label htmlFor="product-offer-end-date">Offer expires on</label>
                <DatePicker
                  name="product-offer-end-date"
                  value={offerExpiresOn}
                  onChange={(date) => setOfferExpiresOn(date)}
                  error={errors.offerExpiresOn}
                  minDate={offerAvailableFrom}
                />
              </section>

              <section className="upload-product-listing__form__grid__item">
                <label htmlFor="product-city">Location City</label>
                <TextInput
                  name="product-city"
                  value={city}
                  onChange={({ target }) => setCity(target.value)}
                  error={errors.city}
                />
              </section>

              <section className="upload-product-listing__form__grid__item">
                <label htmlFor="product-country">Location Country</label>
                <TextInput
                  name="product-country"
                  value={country}
                  onChange={({ target }) => setCountry(target.value)}
                  error={errors.country}
                />
              </section>
            </section>

            <section className="upload-product-listing__form__photos">
              <p>Photos</p>
              <div>
                {renderImages()}
                <FilePicker
                  fileRef={fileRef}
                  fileType="image/*"
                  enableFileLimit={true}
                  onChange={({ target }) => {
                    setImageErrorFalse();
                    handleFileChange(target.files[0]);
                  }}
                  triggerElement={
                    <button
                      className={`${getClassNames('upload-product-listing__form__file-button', {
                        error: errors.images,
                      })} `}
                      onClick={() => fileRef.current.click()}
                      data-testid="add-image-button"
                    />
                  }
                />
              </div>
            </section>

            <section className="upload-product-listing__tips">
              <ul className="upload-product-listing__custom-list">
                <li>Recommended size: 800 x 800 pixels.</li>
                <li>
                  For best results, <b>upload 3 - 4 images</b> for buyers to get a good sense of quality, quantity and
                  state of product.
                </li>
                <li>Use a good mix of closeup images, wide image of available consignment, and other varieties.</li>
                <li>The first image will be the default main product image, so make it a good one.</li>
              </ul>
            </section>

            <section className="upload-product-listing__form__grid__item">
              <label htmlFor="product-information">Additional Information</label>
              <TextInput
                name="product-information"
                value={additionalInformation}
                onChange={({ target }) => setAdditionalInformation(target.value)}
                multiline
              />
            </section>

            <section className="upload-product-listing__form__actions">
              <Button onClick={() => navigate(-1)} secondary>
                Cancel
              </Button>
              {renderSecondaryButton()}
              {renderPrimaryButton()}
            </section>
          </section>
        </section>
      </section>

      {selectedImageIndex !== undefined && (
        <ImageCarousel images={images} startIndex={selectedImageIndex} setStartIndex={setSelectedImageIndex} />
      )}
    </>
  );
};

UploadProductListing.propTypes = {
  classifications: PropTypes.arrayOf(PropTypes.shape({})),
  categories: PropTypes.arrayOf(PropTypes.shape({})),
  types: PropTypes.arrayOf(PropTypes.shape({})),
  metricUnits: PropTypes.arrayOf(PropTypes.shape({})),
  retrieveCategories: PropTypes.func.isRequired,
  retrieveTypes: PropTypes.func.isRequired,
  retrieveMetricUnits: PropTypes.func.isRequired,
  createProductListing: PropTypes.func.isRequired,
  updateProductListing: PropTypes.func.isRequired,
  copyListing: PropTypes.func.isRequired,
  showErrorToast: PropTypes.func.isRequired,
  currencyCode: PropTypes.string.isRequired,
};

UploadProductListing.defaultProps = {
  classifications: [],
  categories: [],
  types: [],
  metricUnits: [],
};

export default UploadProductListing;
